import { useContext } from "react";
import {
  PersonFiltersContext,
  PersonFiltersEventContext,
} from "../../../../context/FiltersContext";
import {
  QueryFilter,
  PersonFilterKeys,
  PersonAdvokatkredsFilterSetting,
} from "../../../../interfaces/filters";
import { PersonDtoFilterInput } from "../../../../interfaces/schema";
import { f } from "../../../../util/f";
import { AccordionSubFilterItem } from "../../../accordion/Accordion";
import DefaultDropdownItem from "../../../dropdown/DefaultDropdownItem";
import SuperDropdown from "../../../dropdown/SuperDropdown";
import AppliedFilterItem from "../../../filter/AppliedFilterItem";
import { advokatkredse } from "../../../../util/advokatkreds";

// helpers
const filterKey: QueryFilter<PersonFilterKeys>["filterKey"] = "advokatkreds";

export default function PersonAdvokatkredsFilter() {
  // context
  const filters = useContext(PersonFiltersContext);
  const updateFilters = useContext(PersonFiltersEventContext);

  // // find active settings
  const settings: PersonAdvokatkredsFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // business
  const advokatkredsSorted = advokatkredse.sort((a, b) =>
    a.name.localeCompare(b.name, "da-DK"),
  );

  // handlers
  function updateFilterHandler(selectedItem: { kode: number; name: string }) {
    // check if selected element already is in settings otherwise add it
    if (!settings?.some((s) => s.id === selectedItem.kode)) {
      settings.push({ id: selectedItem.kode, name: selectedItem.name });
    }

    const filterString = f<PersonDtoFilterInput>()
      .addFilter("primaerPersonFirmaRelation", {
        or: settings.map((set) => {
          return {
            firma: {
              retskredsNrNavigation: {
                advokatkredsNr: {
                  eq: set.id,
                },
              },
            },
          };
        }),
      })
      .build();

    const queryFilter: QueryFilter<
      PersonFilterKeys,
      PersonAdvokatkredsFilterSetting[]
    > = {
      filter: filterString,
      filterKey: filterKey,
      settings: settings,
      parentFilter: "and",
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterHandler(id: PersonAdvokatkredsFilterSetting["id"]) {
    const updatedSettings: PersonAdvokatkredsFilterSetting[] =
      settings.filter((set) => {
        return id !== set.id;
      }) || [];

    const filterString = f<PersonDtoFilterInput>()
      .addFilter("primaerPersonFirmaRelation", {
        or: updatedSettings.map((set) => {
          return {
            firma: {
              retskredsNrNavigation: {
                advokatkredsNr: {
                  eq: set.id,
                },
              },
            },
          };
        }),
      })
      .build();

    const update = updatedSettings.length > 0;

    if (update) {
      updateFilters({
        event: "update",
        queryFilter: {
          filterKey: filterKey,
          filter: filterString,
          settings: updatedSettings,
        },
      });
    } else {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
    }
  }

  return (
    <AccordionSubFilterItem
      title="Advokatkreds"
      value="advokat"
      isActive={!!settings.length}
    >
      {settings && (
        <div className="c-applied-filter-group">
          {settings?.map((filter) => (
            <AppliedFilterItem
              key={filter.id}
              label={filter.name}
              removeFilterHandler={() => removeFilterHandler(filter.id)}
            />
          ))}
        </div>
      )}

      <SuperDropdown
        items={advokatkredsSorted}
        itemsIdKey="name"
        itemToString={(item) => item?.name ?? ""}
        itemToElement={(item) => <DefaultDropdownItem label={`${item.name}`} />}
        placeholder="Vælg advokatkreds ..."
        clearInputAfterSelection={true}
        onSelectedItemEvent={(item) => {
          if (item.selectedItem?.nr && item.selectedItem.name) {
            updateFilterHandler({
              kode: item.selectedItem.nr,
              name: item.selectedItem.name,
            });
          }
        }}
      />
    </AccordionSubFilterItem>
  );
}
